import { X, RefreshCw, Box, Truck, ChevronDown, Key, PlayCircle } from "lucide-react";
import { Language, dictionary } from "../../../utils/dictionary";
import { INTEGRATIONS_UI_CONFIG } from "../config/integrations.config";

const DROPI_LOGO = INTEGRATIONS_UI_CONFIG.dropi.logoSrc;
const FLUXI_LOGO = "https://fluxi.s3.amazonaws.com/prod/assets/fluxi/fluxi-logo.svg";

interface ModalDropiIntegrationProps {
    setModalOpenDropi: (open: boolean) => void;
    handlePostDropiIntegration: (token: string) => void;
    token: string;
    setToken: (token: string) => void;
    language: Language;
}

const ModalDropiIntegration = ({ setModalOpenDropi, handlePostDropiIntegration, token, setToken, language }: ModalDropiIntegrationProps) => {

    const handleSubmit = (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!token.trim()) return;
        handlePostDropiIntegration(token);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-[850px] overflow-hidden flex flex-col md:flex-row relative">
                <button
                    onClick={() => setModalOpenDropi(false)}
                    className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 z-10 md:hidden"
                >
                    <X size={24} />
                </button>

                {/* Left Side - Form */}
                <div className="flex-1 p-6 md:p-8 flex flex-col">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-12 h-12 flex items-center justify-center shrink-0">
                            <img src={DROPI_LOGO} alt="Dropi" className="w-full h-full object-contain" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 leading-tight">
                            {dictionary["dropi-connect-title"]?.[language] || "Conecta tu cuenta Dropi"}
                        </h2>
                    </div>

                    <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                        {dictionary["dropi-connect-description"]?.[language] || "Sincroniza tus productos y órdenes de dropshipping directamente con Fluxi. Esta integración te permite automatizar la gestión de pedidos con proveedores de Dropi."}
                    </p>

                    <div className="space-y-3 mb-6">
                        <div className="flex items-start gap-3">
                            <RefreshCw className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                            <span className="text-sm text-gray-700">
                                {dictionary["dropi-feature-sync"]?.[language] || "Sincronización de inventario de proveedores"}
                            </span>
                        </div>
                        <div className="flex items-start gap-3">
                            <Box className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                            <span className="text-sm text-gray-700">
                                {dictionary["dropi-feature-orders"]?.[language] || "Automatización de órdenes de compra"}
                            </span>
                        </div>
                        <div className="flex items-start gap-3">
                            <Truck className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                            <span className="text-sm text-gray-700">
                                {dictionary["dropi-feature-tracking"]?.[language] || "Trazabilidad de envíos en tiempo real"}
                            </span>
                        </div>
                    </div>

                    <div className="mb-5">
                        <a
                            href="https://entrenamiento.fluxi.co/c/dropi-313905/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-3 p-3 pr-5 rounded-lg bg-[#f0f4ff] group cursor-pointer w-auto"
                        >
                            <div className="w-10 h-10 rounded-full bg-[#5050ea] flex items-center justify-center shrink-0 group-hover:bg-[#3b3bc4] transition-colors shadow-sm">
                                <PlayCircle className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[13px] font-semibold text-gray-800">{dictionary["dropi-video-tutorial"]?.[language] || "¿No sabes cómo conectar Dropi?"}</span>
                                <span className="text-xs text-[#5050ea] font-medium">{dictionary["watch-video-tutorial"]?.[language] || "Ver video tutorial"}</span>
                            </div>
                        </a>
                    </div>

                    <div className="mt-auto pt-4 border-t border-gray-100">
                        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    {dictionary["dropi-token"][language]}
                                </label>
                                <div className="relative">
                                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="text"
                                        value={token}
                                        onChange={(e) => setToken(e.target.value)}
                                        placeholder={dictionary["example-dropi-token"][language]}
                                        className="w-full pl-10 p-2.5 rounded-lg border border-gray-300 focus:ring-blue-100 focus:border-blue-500 focus:outline-none focus:ring-2 transition-all text-sm"
                                    />
                                </div>
                                <p className="text-[11px] text-gray-400 mt-1.5">
                                    {dictionary["dropi-credentials-hint"]?.[language] || "Encuentra este token en la configuración de API de tu cuenta Dropi."}
                                </p>
                            </div>

                            <div className="flex gap-3 mt-2">
                                <button
                                    type="submit"
                                    disabled={!token.trim()}
                                    className="flex-1 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm shadow-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {dictionary["connect"][language]}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setModalOpenDropi(false)}
                                    className="flex-1 bg-white hover:bg-gray-50 text-gray-700 font-medium py-2.5 px-4 rounded-lg border border-gray-200 transition-colors text-sm"
                                >
                                    {dictionary["cancel"][language]}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Right Side - Visual */}
                <div className="hidden md:flex w-[200px] bg-gradient-to-b from-[#eff6ff] to-white relative items-center justify-center py-6 px-4 border-l border-gray-100 shrink-0">
                    <button
                        onClick={() => setModalOpenDropi(false)}
                        className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 z-10 transition-colors"
                    >
                        <X size={18} />
                    </button>

                    <div className="flex flex-col items-center justify-center gap-8 w-full h-full">
                        <div className="bg-white rounded-[24px] shadow-[0_4px_20px_rgba(59,130,246,0.15)] border border-blue-100 p-5 w-24 h-24 flex items-center justify-center z-10 relative">
                            <img src={FLUXI_LOGO} alt="Fluxi" className="w-full h-auto object-contain" />
                        </div>

                        <div className="flex flex-col items-center gap-1 text-blue-300">
                            <ChevronDown strokeWidth={3} size={24} className="animate-[dropiFade_1.5s_infinite_0ms]" />
                            <ChevronDown strokeWidth={3} size={24} className="animate-[dropiFade_1.5s_infinite_200ms]" />
                            <ChevronDown strokeWidth={3} size={24} className="animate-[dropiFade_1.5s_infinite_400ms]" />
                        </div>

                        <div className="bg-white rounded-[24px] shadow-sm border border-gray-200 p-5 w-24 h-24 flex items-center justify-center z-10 relative">
                            <img src={DROPI_LOGO} alt="Dropi" className="w-12 h-12 object-contain" />
                        </div>

                        <style>{`
                            @keyframes dropiFade {
                                0%, 100% { opacity: 0.3; transform: translateY(0); }
                                50% { opacity: 1; transform: translateY(3px); }
                            }
                        `}</style>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalDropiIntegration;
